import { z } from "zod";

const date = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine(
    (value) =>
      Number.isFinite(Date.parse(value)) &&
      new Date(value).toISOString().slice(0, 10) === value,
  );
const amount = z.number().finite().nonnegative();
const base = {
  id: z.string().uuid(),
  vehicleId: z.string().uuid().nullable(),
  title: z.string().trim().min(1).max(200),
  date,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
};
const currency = z.string().regex(/^[A-Z]{3}$/);
export const AutobotVehicleSchema = z.object({
  ...base,
  kind: z.literal("vehicle"),
  details: z.object({
    baselineKm: amount,
    power: z.enum(["electric", "fuel", "hybrid"]),
    remainingKm: amount.nullable(),
    insuranceUntil: date.nullable(),
    inspectionUntil: date.nullable(),
  }),
});
export const AutobotTripSchema = z.object({
  ...base,
  kind: z.literal("trip"),
  vehicleId: z.string().uuid(),
  details: z
    .object({
      origin: z.string().trim().min(1).max(200),
      destination: z.string().trim().min(1).max(200),
      startKm: amount,
      endKm: amount,
      durationMinutes: amount,
      energy: amount.nullable(),
      unit: z.enum(["kWh", "L"]),
    })
    .refine((value) => value.endKm >= value.startKm),
});
export const AutobotEnergySchema = z.object({
  ...base,
  kind: z.literal("energy"),
  vehicleId: z.string().uuid(),
  details: z.object({
    odometerKm: amount,
    quantity: z.number().positive().finite(),
    unit: z.enum(["kWh", "L"]),
    cost: amount,
    currency,
    full: z.boolean(),
  }),
});
export const AutobotMaintenanceSchema = z.object({
  ...base,
  kind: z.literal("maintenance"),
  vehicleId: z.string().uuid(),
  details: z.object({
    odometerKm: amount,
    cost: amount,
    currency,
    nextDate: date.nullable(),
    nextKm: amount.nullable(),
    notes: z.string().max(4000),
  }),
});
export const AutobotRuleSchema = z.object({
  ...base,
  kind: z.literal("rule"),
  vehicleId: z.string().uuid(),
  details: z
    .object({
      trigger: z.enum(["daily", "weekly", "date", "odometer"]),
      time: z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/),
      weekday: z.number().int().min(0).max(6),
      dueDate: date.nullable(),
      thresholdKm: amount.nullable(),
      enabled: z.boolean(),
      action: z.literal("reminder"),
    })
    .superRefine((value, context) => {
      if (
        (value.trigger === "date" && !value.dueDate) ||
        (value.trigger === "odometer" && value.thresholdKm === null)
      )
        context.addIssue({
          code: "custom",
          message: "Trigger requires a date or odometer threshold",
        });
    }),
});
export const AutobotRunSchema = z.object({
  ...base,
  kind: z.literal("run"),
  vehicleId: z.string().uuid(),
  details: z.object({
    ruleId: z.string().uuid(),
    occurrence: z.string().min(1).max(128),
    trigger: z.enum(["daily", "weekly", "date", "odometer"]),
    result: z.literal("recorded"),
    odometerKm: amount,
    acknowledged: z.boolean(),
  }),
});
export const AutobotRecordSchema = z.discriminatedUnion("kind", [
  AutobotVehicleSchema,
  AutobotTripSchema,
  AutobotEnergySchema,
  AutobotMaintenanceSchema,
  AutobotRuleSchema,
  AutobotRunSchema,
]);
export type AutobotRecord = z.infer<typeof AutobotRecordSchema>;
export type AutobotVehicle = z.infer<typeof AutobotVehicleSchema>;
export type AutobotRule = z.infer<typeof AutobotRuleSchema>;
